// COST-02 / COST-03 cost entry: labour (hours x rate) or an expense / credit.
import { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { toast } from '@/lib/toast'
import { COST_TYPE_LABEL, costingApi, designMode, type CostLine, type CostType, type LaborRateRow, type NewCostEntry } from './api'

const EXPENSE_TYPES: CostType[] = ['MATERIAL', 'SUBCONTRACTOR', 'OTHER', 'LABOR']

export function CostEntryForm({
  jobId,
  lines,
  people,
  onSaved,
}: {
  jobId: string
  lines: CostLine[]
  people: LaborRateRow[]
  onSaved: () => void
}) {
  const [kind, setKind] = useState<'LABOR' | 'EXPENSE'>('LABOR')
  const [userId, setUserId] = useState('')
  const [hours, setHours] = useState('')
  const [rate, setRate] = useState('')
  const [amount, setAmount] = useState('')
  const [costType, setCostType] = useState<CostType>('MATERIAL')
  const [vendor, setVendor] = useState('')
  const [reimbursable, setReimbursable] = useState(false)
  const [receiptUrl, setReceiptUrl] = useState('')
  const [receiptName, setReceiptName] = useState('')
  const [uploading, setUploading] = useState(false)
  const [lineId, setLineId] = useState('')
  const [description, setDescription] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function uploadReceipt(file: File) {
    if (file.size > 15 * 1024 * 1024) { setError('Receipts must be 15 MB or smaller.'); return }
    setError(null); setUploading(true)
    try { const url = await costingApi.uploadReceipt(jobId, file); setReceiptUrl(url); setReceiptName(file.name) }
    catch (err) { toast.error(err, 'upload the receipt') }
    finally { setUploading(false) }
  }
  const [saving, setSaving] = useState(false)

  const person = people.find((p) => p.id === userId)

  function build(): NewCostEntry | string {
    const common = { estimateLineId: lineId || null, description: description.trim() || null }
    if (kind === 'LABOR') {
      const h = Number(hours)
      if (!hours || !Number.isFinite(h) || h <= 0) return 'Enter the hours worked.'
      const r = rate.trim() === '' ? undefined : Number(rate)
      if (r !== undefined && (!Number.isFinite(r) || r < 0)) return 'Enter a valid hourly rate.'
      if (r === undefined && person?.laborCostRate == null) return 'Pick someone with a labour rate, or enter a rate.'
      return { kind, userId: userId || null, hours: h, ...(r !== undefined ? { rate: r } : {}), ...common }
    }
    const a = Number(amount)
    if (amount.trim() === '' || !Number.isFinite(a) || a === 0) return 'Enter an amount (negative for a credit or return).'
    const url = receiptUrl.trim()
    if (url && !/^https?:\/\//i.test(url) && !url.startsWith('/uploads/')) return 'The receipt link must start with http:// or https://.'
    return { kind, costType, amount: a, vendor: vendor.trim() || null, reimbursable, receiptUrl: url || null, ...common }
  }

  async function submit() {
    const body = build()
    if (typeof body === 'string') { setError(body); return }
    setError(null)
    setSaving(true)
    try {
      await costingApi.addEntry(jobId, body)
      toast.success(jobId === '__design-job-costing' ? 'Demo cost added temporarily' : kind === 'LABOR' ? 'Labour added' : 'Expense added')
      setHours(''); setRate(''); setAmount(''); setVendor(''); setReceiptUrl(''); setReceiptName(''); setDescription(''); setReimbursable(false)
      onSaved()
    } catch (err) {
      toast.error(err, 'add the cost')
    } finally {
      setSaving(false)
    }
  }

  const select = { className: 'w-full min-w-0 rounded-lg border px-3 py-2 text-base', style: { background: 'transparent' } }

  return (
    <form
      className="job-costing-entry"
      aria-label="Add cost"
      onSubmit={(e) => { e.preventDefault(); void submit() }}
      style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      <div role="radiogroup" aria-label="Cost kind" style={{ display: 'flex', gap: 8 }}>
        {(['LABOR', 'EXPENSE'] as const).map((k) => (
          <Button key={k} type="button" size="sm" variant={kind === k ? 'primary' : 'secondary'} role="radio" aria-checked={kind === k} onClick={() => setKind(k)}>
            {k === 'LABOR' ? 'Labour' : 'Expense'}
          </Button>
        ))}
      </div>

      {kind === 'LABOR' ? (
        <>
          <label className="text-sm">
            Team member
            <select {...select} aria-label="Team member" value={userId} onChange={(e) => setUserId(e.target.value)}>
              <option value="">Someone else (enter a rate)</option>
              {people.map((p) => (
                <option key={p.id} value={p.id}>
                  {`${p.firstName} ${p.lastName}`.trim()}{p.laborCostRate != null ? ` · $${p.laborCostRate}/h` : ' · no rate'}
                </option>
              ))}
            </select>
          </label>
          {person && <p className="text-sm" aria-live="polite">Selected team member: {`${person.firstName} ${person.lastName}`.trim() || person.email}</p>}
          <Input label="Hours" inputMode="decimal" value={hours} onChange={(e) => setHours(e.target.value)} />
          <Input
            label="Hourly rate"
            inputMode="decimal"
            value={rate}
            placeholder={person?.laborCostRate != null ? String(person.laborCostRate) : ''}
            helperText={person?.laborCostRate != null ? 'Leave blank to use their rate from Settings → Team.' : undefined}
            onChange={(e) => setRate(e.target.value)}
          />
        </>
      ) : (
        <>
          <label className="text-sm">
            Cost type
            <select {...select} aria-label="Cost type" value={costType} onChange={(e) => setCostType(e.target.value as CostType)}>
              {EXPENSE_TYPES.map((t) => <option key={t} value={t}>{COST_TYPE_LABEL[t]}</option>)}
            </select>
          </label>
          <Input label="Amount" inputMode="decimal" value={amount} helperText="Use a negative amount for a credit or return." onChange={(e) => setAmount(e.target.value)} />
          <Input label="Vendor" value={vendor} onChange={(e) => setVendor(e.target.value)} />
          <label className="text-sm" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            Receipt photo or PDF
            <input
              type="file"
              aria-label="Receipt photo or PDF"
              accept="image/*,application/pdf,.heic,.heif"
              capture="environment"
                disabled={uploading || designMode()}
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void uploadReceipt(f) }}
            />
            {uploading ? <span role="status">Uploading receipt…</span> : receiptName ? <span data-testid="receipt-attached">Attached: {receiptName}</span> : null}
          </label>
          <Input label="Or receipt link" value={receiptName ? '' : receiptUrl} disabled={!!receiptName} onChange={(e) => setReceiptUrl(e.target.value)} />
          <label className="text-sm" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="checkbox" checked={reimbursable} onChange={(e) => setReimbursable(e.target.checked)} />
            Reimbursable to the team member
          </label>
        </>
      )}

      {lines.length > 0 && (
        <label className="text-sm">
          Estimate line
          <select {...select} aria-label="Estimate line" value={lineId} onChange={(e) => setLineId(e.target.value)}>
            <option value="">Not tied to a line</option>
            {lines.map((l) => <option key={l.id} value={l.id}>{l.description}</option>)}
          </select>
          {lineId && <span className="block mt-1">Selected estimate line: {lines.find((line) => line.id === lineId)?.description}</span>}
        </label>
      )}
      <Input label="Note" value={description} onChange={(e) => setDescription(e.target.value)} />

      {error && <p role="alert" style={{ fontSize: 13, color: 'var(--blush-text)' }}>{error}</p>}
      <Button type="submit" variant="primary" size="md" needsNetwork loading={saving} disabled={uploading}>
        {kind === 'LABOR' ? 'Add labour' : 'Add expense'}
      </Button>
    </form>
  )
}
